/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { organizationsByName, organizationsByDomain } from "./queries";
import { Button, Form, Checkbox, Segment } from "semantic-ui-react";
import cStyles from "./../../Components.module.css";
import { authCheckCallApi, getUrlParam } from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import useForm from "react-hook-form";
import OrganizationsList from "./OrganizationsList";
import ConfirmationDialog from "../../ConfirmationDialog";
import CreateOrgDialog from "./CreateOrgDialog";
import { Ico } from "../../Icon/Icon";

interface OrganizationsProps {
  ro: boolean;
  showSelectedUser: (user: string) => void;
  setSelectedOrg: (org: string | null) => void;
  selectedOrg: string | null;
}

const Organizations: React.FC<OrganizationsProps> = ({
  ro,
  showSelectedUser,
  selectedOrg,
}) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const client = useApolloClient();
  const { register, handleSubmit, errors, setValue, getValues } =
    useForm<FormData>();
  const [orgData, setOrgData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchByDomain, setSearchByDomain] = useState<boolean>(false);
  const [create, setCreate] = useState(false);

  type FormData = {
    orgName: string;
  };
  const emptyInputs = {
    orgName: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const searchOrganizations = () => {
    setLoading(true);
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: searchByDomain ? organizationsByDomain : organizationsByName,
          variables: {
            name: getValues().orgName.toLocaleLowerCase()
          },
        })
        .then((result) => {
          setOrgData(result.data);
        })
        .catch((e) => {
          setMessage({
            title: "Search Organizations",
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    const orgParam = getUrlParam("o") || selectedOrg;
    if (orgParam) {
      setValue("orgName", orgParam);
      searchOrganizations();
    }
  }, [selectedOrg]);

  return (
    <Segment basic>
      <Form
        onSubmit={handleSubmit(searchOrganizations)}
        style={{
          display: "flex",
          alignItems: "baseline",
        }}
      >
        <Form.Group inline>
          <Form.Field required error={!!errors.orgName}>
            <input
              autoComplete="off"
              name="orgName"
              key="orgName"
              ref={register({ required: "Field is required" })}
              onChange={handleInputChange}
              defaultValue={inputs.orgName}
              placeholder="Organization name"
              className={cStyles.input}
            />
          </Form.Field>
        </Form.Group>

        <Checkbox
          style={{ marginLeft: "2rem" }}
          label="Search by Domain"
          onChange={() => setSearchByDomain(!searchByDomain)}
          checked={searchByDomain}
          disabled={loading}
        />

        <span
          style={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            primary
            className={cStyles.buttonPrime}
            style={{ marginLeft: "10px" }}
            type="submit"
            loading={loading}
            disabled={loading}
          >
            <Ico name="search" style={{ marginRight: "10px" }} />
            Search Organizations
          </Button>

          <Button
            primary
            type="button"
            disabled={ro}
            className={cStyles.buttonPrime}
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setCreate(true);
            }}
          >
            Create
          </Button>
        </span>
      </Form>
      <OrganizationsList
        orgData={orgData && orgData.orgs}
        onRefresh={searchOrganizations}
        ro={ro}
        showSelectedUser={showSelectedUser}
      />
      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
      {create && <CreateOrgDialog onClose={() => setCreate(false)} />}
    </Segment>
  );
};

export default Organizations;
